import React from "react";

import Lightbox from "yet-another-react-lightbox";
import Counter from "yet-another-react-lightbox/plugins/counter";
import Video from "yet-another-react-lightbox/plugins/video";
import Zoom from "yet-another-react-lightbox/plugins/zoom";

import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/counter.css";

import { BulletinMediaItem } from "@/components/alerts/add-v2/model/types";

/**
 * A file at full size: pinch, wheel and double-tap to zoom, swipe or arrow keys between files.
 *
 * `yet-another-react-lightbox` rather than the portal's three existing viewers — `CompareMediaDisplay`
 * (what `DisplayAlert` and the public board use), `ExpandedMediaDisplay` (online reporting) and
 * `AttachmentPreviewModal` (its file questions), all variations on one MUI `Modal` with arrow buttons.
 * The reasons for a fourth thing, which is not a decision to take lightly:
 *
 * - **Officers are on tablets.** Those three zoom by button only; none of them handles a pinch or a
 *   swipe, and `CompareMediaDisplay` passes no `onClose` to its `Modal`, so Escape does not close it.
 * - **They are shared.** Each has two or three callers outside this directory, and its props are two
 *   pieces of redundant state (`isSlideShowActive` *and* `selectedIndex`) kept in step by an effect.
 *   Fixing that where it lives means touching the old bulletin view, the public board and online
 *   reporting at once.
 * - **Video.** A bulletin holds both, and the Video plugin covers it without a second code path.
 *
 * The consistency cost is real and worth writing down: until the view flow moves onto this too, a
 * photo enlarges differently depending on whether the officer is writing the bulletin or reading it.
 * Moving it is a small change — the three viewers all take a src and an index — and it is the reason
 * this file has no add-v2 knowledge in it beyond `BulletinMediaItem`.
 */

interface MediaLightboxProps {
  /** Already filtered to `isViewable` (its own module), in the order they are shown. */
  items: BulletinMediaItem[];
  /** Which one to open on. `-1` closes the lightbox, which is what the library reads. */
  index: number;
  onClose: () => void;
}

export const MediaLightbox: React.FC<MediaLightboxProps> = ({
  items,
  index,
  onClose,
}) => (
  <Lightbox
    open={index >= 0}
    close={onClose}
    index={Math.max(index, 0)}
    plugins={[Zoom, Video, Counter]}
    // Finite: a bulletin's files are a short list, and wrapping past the end reads as a bug.
    carousel={{ finite: true }}
    // Both are only offered when there is somewhere to go, so a single file shows neither.
    render={{
      buttonPrev: items.length > 1 ? undefined : () => null,
      buttonNext: items.length > 1 ? undefined : () => null,
    }}
    slides={items.map((item) =>
      item.contentType.startsWith("video/")
        ? {
            type: "video" as const,
            sources: [{ src: item.url, type: item.contentType }],
            // The thumbnail is the poster frame, so a video looks like its own still until it plays.
            poster: item.thumbnailUrl ?? undefined,
          }
        : { src: item.url, alt: item.filename },
    )}
  />
);
